import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import { useResettableState } from 'hooks/useResettableState';
import { useLocation, useNavigate } from 'react-router-dom';
import { InspectionTemplateItemModel } from 'components/dataProviders/withRequiredInspectionTemplate/types';
import { InspectionTemplateState } from 'components/common/dataGrid/columnsFactory/types';
import { useTemplateCardRefs } from 'contexts/TemplateCardRefsContext';
import {
  UrlParamName,
  useAppUrlParams,
} from 'hooks/navigation/useAppUrlParams';
import { useProject } from 'store/project';
import { useInspectionStore } from 'store/inspection';
import {
  NO_FILTERED_TEMPLATES_ERROR,
  canFindOnTemplatesList,
  determineActiveTemplate,
  scrollToActiveCard,
  setActiveTemplateWithScroll,
  updateTemplateUrl,
  useLastSelectedInspectionTemplate,
  useScrollToActiveTemplate,
} from '../helpers';
import { TemplateListError } from '../types';

export interface UseTemplateListSelectionParams {
  items: InspectionTemplateItemModel[];
  filteredItems: InspectionTemplateItemModel[];
  loading: boolean;
  searchPhrase: string;
  debouncedSearchPhrase: string;
}

export interface UseTemplateListSelectionResult {
  active: string | undefined;
  activeRef: MutableRefObject<string | undefined>;
  setActive: (id?: string) => void;
  error: TemplateListError | undefined;
  setCanScroll: (value: boolean) => void;
  selectTemplate: (template: InspectionTemplateItemModel) => void;
  loadingLastSelected: boolean;
}

export function useTemplateListSelection({
  items,
  filteredItems,
  loading,
  searchPhrase,
  debouncedSearchPhrase,
}: UseTemplateListSelectionParams): UseTemplateListSelectionResult {
  const { getCard, getContainer } = useTemplateCardRefs();
  const activeRef = useRef<string | undefined>(undefined);
  const prevSearchPhraseRef = useRef('');
  const inspectionTemplatesListState =
    useInspectionStore.use.inspectionTemplatesListState();
  const prevInspectionTemplatesListStateRef = useRef(
    inspectionTemplatesListState
  );
  const inspectionTemplatesItems =
    useInspectionStore.use.inspectionTemplates().items;
  const setInspectionTemplatesListState =
    useInspectionStore.use.setInspectionTemplatesListState();

  const location = useLocation();
  const navigate = useNavigate();
  const id = useAppUrlParams(UrlParamName.INSPECTION_TEMPLATE_ID);
  const templateIdFromState = (location.state as { templateId?: string })
    ?.templateId;
  const project = useProject();

  // Navigating directly to a template's URL (e.g. a pasted link) must show
  // the toggle state that actually makes it visible in the filtered list,
  // otherwise it can never be resolved as "active" and the view spins forever.
  // This must only happen once per `id` - otherwise it fights the user: toggling
  // published/archived by hand while the same archived `id` stays in the URL
  // would get immediately reverted by this effect re-running on state change.
  // inspectionTemplatesListState is read via a ref (not a dependency) so a
  // manual toggle never re-triggers this sync.
  const syncedTemplateIdRef = useRef<string | undefined>(undefined);
  const inspectionTemplatesListStateRef = useRef(inspectionTemplatesListState);

  useEffect(() => {
    inspectionTemplatesListStateRef.current = inspectionTemplatesListState;
  }, [inspectionTemplatesListState]);

  useEffect(() => {
    if (!id || syncedTemplateIdRef.current === id) {
      return;
    }

    const targetTemplate = inspectionTemplatesItems.find(
      (template) => template._id === id
    );
    if (!targetTemplate) {
      return;
    }

    syncedTemplateIdRef.current = id;

    const requiredState = targetTemplate.deleted
      ? InspectionTemplateState.archived
      : InspectionTemplateState.published;

    if (requiredState !== inspectionTemplatesListStateRef.current) {
      setInspectionTemplatesListState(requiredState);
    }
  }, [id, inspectionTemplatesItems, setInspectionTemplatesListState]);

  const canSetErrors = !loading;
  // A new search phrase starts without the previous search's error.
  const [error, setError] = useResettableState<TemplateListError | undefined>(searchPhrase, () => undefined);
  const [canScroll, setCanScroll] = useState(false);
  const [active, _setActive] = useState<string | undefined>(undefined);

  const setActive = useCallback((activeId?: string) => {
    activeRef.current = activeId;
    _setActive(activeId);
  }, []);

  const { loading: loadingLastSelected } = useLastSelectedInspectionTemplate({
    templates: items,
    projectId: project._id,
  });

  useEffect(() => {
    if (loading) {
      return;
    }

    const stateChanged =
      prevInspectionTemplatesListStateRef.current !==
      inspectionTemplatesListState;
    if (!stateChanged) {
      return;
    }

    prevInspectionTemplatesListStateRef.current = inspectionTemplatesListState;

    if (filteredItems.length > 0) {
      const firstTemplate = filteredItems[0];
      setActiveTemplateWithScroll(
        firstTemplate._id,
        activeRef,
        setActive,
        setCanScroll,
        setError
      );
      updateTemplateUrl(navigate, project._id, firstTemplate._id, true);
    } else {
      setActive(undefined);
      setError(undefined);
    }
  }, [
    filteredItems,
    inspectionTemplatesListState,
    loading,
    navigate,
    project._id,
    setActive,
  ]);

  useEffect(() => {
    const normalizedPhrase = debouncedSearchPhrase;
    const hasActive = filteredItems.find(
      (template) => template._id === active
    );

    if (
      filteredItems.length !== 0 &&
      !hasActive &&
      prevSearchPhraseRef.current !== normalizedPhrase
    ) {
      setActive(filteredItems[0]._id);
    }

    if (filteredItems.length !== 0 && hasActive && active) {
      const cardElement = getCard(active);
      const container = getContainer();
      scrollToActiveCard(cardElement, container);
    }

    if (filteredItems.length === 0 && items.length > 0) {
      setError(NO_FILTERED_TEMPLATES_ERROR);
    }

    prevSearchPhraseRef.current = normalizedPhrase;
  }, [
    debouncedSearchPhrase,
    filteredItems,
    items.length,
    active,
    setActive,
    getCard,
    getContainer,
  ]);

  useEffect(() => {
    if (loadingLastSelected || !canSetErrors) {
      setError(undefined);
      return;
    }

    if (id && inspectionTemplatesItems.length === 0) {
      setError(undefined);
      return;
    }

    const isActiveValid = active && canFindOnTemplatesList(items, active);

    if (isActiveValid && !id) {
      return;
    }

    if (active && !isActiveValid) {
      setActive(undefined);
    }

    const result = determineActiveTemplate(
      id,
      templateIdFromState,
      items,
      active,
      inspectionTemplatesItems,
      inspectionTemplatesListState
    );

    if (result.templateId) {
      setActiveTemplateWithScroll(
        result.templateId,
        activeRef,
        setActive,
        setCanScroll,
        setError
      );

      if (result.shouldUpdateUrl) {
        updateTemplateUrl(navigate, project._id, result.templateId, true);
      }
    } else {
      setActive(undefined);
      setCanScroll(false);
      setError(result.error);
    }
  }, [
    id,
    templateIdFromState,
    items,
    loading,
    loadingLastSelected,
    canSetErrors,
    active,
    setActive,
    navigate,
    project._id,
    inspectionTemplatesItems,
    inspectionTemplatesListState,
  ]);

  useScrollToActiveTemplate(
    active,
    canScroll,
    loading,
    filteredItems,
    project._id,
    setCanScroll,
    getCard,
    getContainer
  );

  const handleSelectTemplate = useCallback(
    (value: InspectionTemplateItemModel): void => {
      const isAlreadyActive = active === value._id;

      if (isAlreadyActive) {
        return;
      }

      setActiveTemplateWithScroll(
        value._id,
        activeRef,
        setActive,
        setCanScroll,
        setError
      );

      updateTemplateUrl(navigate, project._id, value._id, false);
    },
    [active, navigate, project._id, setActive]
  );

  return {
    active,
    activeRef,
    setActive,
    error,
    setCanScroll,
    selectTemplate: handleSelectTemplate,
    loadingLastSelected,
  };
}
